import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Receipt, Info, FileText, Building } from 'lucide-react';
import Seo from '@/components/Seo';

const STAMP_DUTY = {
  Maharashtra: { male: 6, female: 5, registration: 1 },
  Karnataka: { male: 5, female: 5, registration: 1 },
  Delhi: { male: 6, female: 4, registration: 1 },
  Tamil_Nadu: { male: 7, female: 7, registration: 4 },
  Telangana: { male: 4, female: 4, registration: 0.5 },
  Uttar_Pradesh: { male: 7, female: 6, registration: 1 },
  Gujarat: { male: 4.9, female: 4.9, registration: 1 },
  Haryana: { male: 7, female: 5, registration: 1 },
  West_Bengal: { male: 6, female: 6, registration: 1 },
  Rajasthan: { male: 6, female: 5, registration: 1 },
  Kerala: { male: 8, female: 8, registration: 2 },
  Punjab: { male: 7, female: 5, registration: 1 },
  Madhya_Pradesh: { male: 7.5, female: 7.5, registration: 3 },
  Goa: { male: 5, female: 4, registration: 3 },
};

const formatCr = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

const StampDutyCalculatorPage = () => {
  const [sp] = useSearchParams();
  const [propertyValue, setPropertyValue] = useState(Number(sp.get('value')) || 7500000);
  const [state, setState] = useState('Maharashtra');
  const [gender, setGender] = useState('male');
  const [propertyType, setPropertyType] = useState('residential');

  const result = useMemo(() => {
    const rates = STAMP_DUTY[state];
    const stampPct = rates[gender];
    const regPct = rates.registration;
    let stamp = (propertyValue * stampPct) / 100;
    let reg = (propertyValue * regPct) / 100;
    // Commercial typically attracts higher stamp (+1%)
    if (propertyType === 'commercial') stamp = (propertyValue * (stampPct + 1)) / 100;
    const total = stamp + reg;
    return { stamp, reg, total, stampPct: propertyType === 'commercial' ? stampPct + 1 : stampPct, regPct };
  }, [propertyValue, state, gender, propertyType]);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid="stamp-duty-page">
      <Seo title="Stamp Duty Calculator (India)" description="Calculate stamp duty and registration charges for 14 Indian states. Includes gender-based rebates and commercial premiums." path="/stamp-duty-calculator" />
      <div className="container-luxury max-w-5xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs font-medium mb-3">
            <Receipt className="h-3.5 w-3.5" /> India · State-wise rates
          </div>
          <h1 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-3">
            Stamp Duty &amp; Registration Calculator
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Calculate the stamp duty and registration fees you'll pay when buying property — varies by state, gender, and type.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
            <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold text-[#0F172A] mb-6 flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#D4AF37]" /> Property Details
            </h2>
            <div className="space-y-5">
              <div>
                <Label className="text-sm">Property value</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    value={propertyValue}
                    onChange={(e) => setPropertyValue(Number(e.target.value) || 0)}
                    className="pl-7"
                    data-testid="stamp-property-value"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{formatCr(propertyValue)}</p>
              </div>

              <div>
                <Label className="text-sm">State</Label>
                <Select value={state} onValueChange={setState}>
                  <SelectTrigger className="mt-1" data-testid="stamp-state-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(STAMP_DUTY).map((s) => (
                      <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Buyer</Label>
                  <Select value={gender} onValueChange={setGender}>
                    <SelectTrigger className="mt-1" data-testid="stamp-gender-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male / Joint</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Property type</Label>
                  <Select value={propertyType} onValueChange={setPropertyType}>
                    <SelectTrigger className="mt-1" data-testid="stamp-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm flex gap-2 text-xs text-blue-900">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Many states offer 1–2% rebate for female buyers. Verify current rates with your local Sub-Registrar's office.</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-[#0F172A] text-white p-8 rounded-sm shadow-md" data-testid="stamp-duty-result">
              <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Total payable</p>
              <p className="font-['Plus_Jakarta_Sans'] text-5xl md:text-6xl font-bold text-[#D4AF37] mb-1">
                {formatCr(result.total)}
              </p>
              <p className="text-white/70 text-sm">
                Approx. {((result.total / propertyValue) * 100).toFixed(2)}% of property value
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-5 rounded-sm border border-border/50">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Stamp Duty</p>
                <p className="font-bold text-[#0F172A] text-2xl">{formatCr(result.stamp)}</p>
                <p className="text-xs text-muted-foreground">{result.stampPct}% of value</p>
              </div>
              <div className="bg-white p-5 rounded-sm border border-border/50">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Registration</p>
                <p className="font-bold text-[#0F172A] text-2xl">{formatCr(result.reg)}</p>
                <p className="text-xs text-muted-foreground">{result.regPct}% of value</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-sm border border-border/50">
              <h3 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2 text-sm">
                <Building className="h-4 w-4 text-[#D4AF37]" /> What's included
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1.5">
                <li>• Stamp duty paid to state government</li>
                <li>• Property registration fee</li>
                <li>• Excludes legal fees, brokerage, and society charges</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StampDutyCalculatorPage;
